import { VOICE_PARTS } from '@corocanto/scoring';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import {
  invitationLink,
  type SingerRoles,
  useChangeMember,
  useChangeReferencePitch,
  useCreateInvitation,
  useRemoveMember,
} from '../api/members';
import type { Invitation, Member, VoicePart } from '../api/types';
import ui from '../styles/ui.module.css';
import { Dialog } from './Dialog';
import styles from './MemberDialogs.module.css';

function errorText(error: unknown, t: (key: string, options?: Record<string, unknown>) => string): string | null {
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : undefined;
  return t(`apiErrors.${code ?? 'unknown'}`, { defaultValue: t('apiErrors.unknown') });
}

/** Copies text, and says so for a moment on the button. */
export function CopyButton({ text, label, className }: { text: string; label: string; className?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={className ?? ui.button}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        });
      }}
    >
      {copied ? t('members.invitations.copied') : label}
    </button>
  );
}

/** Part, and the singer roles: chorist, and section leader of that part. */
function PartAndRoles({ value, onChange }: { value: SingerRoles; onChange: (value: SingerRoles) => void }) {
  const { t } = useTranslation();
  return (
    <>
      <label className={ui.field}>
        <span className={ui.label}>{t('members.inviteDialog.part')}</span>
        <select
          className={`${ui.select} ${styles.part}`}
          value={value.part}
          onChange={(e) => onChange({ ...value, part: e.target.value as VoicePart })}
        >
          {VOICE_PARTS.map((p) => (
            <option key={p} value={p}>
              {t(`parts.${p}`)}
            </option>
          ))}
        </select>
      </label>
      <fieldset className={styles.roles}>
        <legend className={ui.label}>{t('members.inviteDialog.roles')}</legend>
        <label className={styles.check}>
          <input type="checkbox" checked={value.chorist} onChange={(e) => onChange({ ...value, chorist: e.target.checked })} />
          {t('roles.CHORIST')}
        </label>
        <label className={styles.check}>
          <input type="checkbox" checked={value.sectionLeader} onChange={(e) => onChange({ ...value, sectionLeader: e.target.checked })} />
          {t('members.inviteDialog.sectionLeaderOf', { part: t(`parts.${value.part}`) })}
        </label>
      </fieldset>
    </>
  );
}

/** "+ Invite" (wireframe 16): one person at a time; the link is shown at once, to copy and send. */
export function InviteDialog({ choirId, open, onClose }: { choirId: string; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateInvitation(choirId);
  const [name, setName] = useState('');
  const [roles, setRoles] = useState<SingerRoles>({ part: 'S', chorist: true, sectionLeader: false });
  const [email, setEmail] = useState('');
  const [made, setMade] = useState<Invitation | null>(null);

  function reset() {
    setName('');
    setRoles({ part: 'S', chorist: true, sectionLeader: false });
    setEmail('');
    setMade(null);
    create.reset();
  }

  function close() {
    reset();
    onClose();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    create.mutate({ displayName: name.trim(), email, ...roles }, { onSuccess: setMade });
  }

  const ready = name.trim() && (roles.chorist || roles.sectionLeader);

  return (
    <Dialog open={open} title={t('members.inviteDialog.title')} onClose={close} data-testid="invite-dialog">
      <p className={styles.intro}>{t('members.inviteDialog.intro')}</p>
      {made ? (
        <div className={styles.result} data-testid="invitation-result">
          <p className={styles.resultTitle}>
            {t('members.inviteDialog.result', { name: made.displayName, part: t(`parts.${made.part}`) })}
          </p>
          <div className={styles.linkRow}>
            <input
              className={ui.input}
              readOnly
              value={invitationLink(made.token)}
              aria-label={t('members.inviteDialog.link')}
              onFocus={(e) => e.target.select()}
            />
            <CopyButton text={invitationLink(made.token)} label={t('members.inviteDialog.copy')} />
          </div>
          <p className={styles.resultHint}>{t('members.inviteDialog.resultHint')}</p>
          <div className={styles.buttons}>
            <button type="button" className={ui.button} onClick={reset}>
              {t('members.inviteDialog.another')}
            </button>
            <button type="button" className={`${ui.button} ${ui.primary}`} onClick={close}>
              {t('members.inviteDialog.done')}
            </button>
          </div>
        </div>
      ) : (
        <form className={styles.form} onSubmit={submit} noValidate>
          <label className={ui.field}>
            <span className={ui.label}>{t('members.inviteDialog.name')}</span>
            <input className={ui.input} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </label>
          <PartAndRoles value={roles} onChange={setRoles} />
          <label className={ui.field}>
            <span className={ui.label}>{t('members.inviteDialog.email')}</span>
            <input className={ui.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          {create.isError && (
            <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
              {errorText(create.error, t)}
            </div>
          )}
          <div className={styles.buttons}>
            <button type="button" className={ui.button} onClick={close}>
              {t('common.cancel')}
            </button>
            <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={!ready || create.isPending}>
              {create.isPending ? t('members.inviteDialog.making') : t('members.inviteDialog.make')}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

/** "Change part and roles" on a member's row. */
export function EditMemberDialog({ choirId, member, onClose }: { choirId: string; member: Member | null; onClose: () => void }) {
  const { t } = useTranslation();
  const change = useChangeMember(choirId);
  const [roles, setRoles] = useState<SingerRoles | null>(null);
  const value: SingerRoles = roles ?? {
    part: member?.defaultPart ?? 'S',
    chorist: !!member?.roles.includes('CHORIST'),
    sectionLeader: !!member?.roles.includes('SECTION_LEADER'),
  };

  function close() {
    setRoles(null);
    change.reset();
    onClose();
  }

  return (
    <Dialog open={!!member} title={t('members.editDialog.title')} onClose={close} data-testid="edit-member-dialog">
      {member && (
        <form
          className={styles.form}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            change.mutate({ userId: member.userId, ...value }, { onSuccess: close });
          }}
        >
          <p className={styles.who}>{member.displayName}</p>
          <PartAndRoles value={value} onChange={setRoles} />
          {change.isError && (
            <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
              {errorText(change.error, t)}
            </div>
          )}
          <div className={styles.buttons}>
            <button type="button" className={ui.button} onClick={close}>
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className={`${ui.button} ${ui.primary}`}
              disabled={!(value.chorist || value.sectionLeader) || change.isPending}
            >
              {change.isPending ? t('members.editDialog.saving') : t('members.editDialog.save')}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

/** "Remove from the choir": the account and this choir's submissions, grades and comments stay. */
export function RemoveMemberDialog({
  choirId,
  choirName,
  member,
  onClose,
}: {
  choirId: string;
  choirName: string;
  member: Member | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const remove = useRemoveMember(choirId);

  function close() {
    remove.reset();
    onClose();
  }

  return (
    <Dialog open={!!member} title={t('members.removeDialog.title')} onClose={close} data-testid="remove-member-dialog">
      {member && (
        <div className={styles.form}>
          <p className={styles.text}>{t('members.removeDialog.text', { name: member.displayName, choir: choirName })}</p>
          {remove.isError && (
            <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
              {errorText(remove.error, t)}
            </div>
          )}
          <div className={styles.buttons}>
            <button type="button" className={ui.button} onClick={close}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className={`${ui.button} ${styles.dangerButton}`}
              disabled={remove.isPending}
              onClick={() => remove.mutate(member.userId, { onSuccess: close })}
            >
              {t('members.removeDialog.confirm')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** The choir's reference pitch, A = … Hz. */
export function PitchDialog({ choirId, current, open, onClose }: { choirId: string; current: number; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const change = useChangeReferencePitch(choirId);
  const [text, setText] = useState<string | null>(null);
  const value = text ?? String(current);
  const hz = Number(value);
  const valid = value.trim() !== '' && Number.isFinite(hz) && hz >= 400 && hz <= 480;

  function close() {
    setText(null);
    change.reset();
    onClose();
  }

  return (
    <Dialog open={open} title={t('members.pitchDialog.title')} onClose={close} data-testid="pitch-dialog">
      <form
        className={styles.form}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) change.mutate(hz, { onSuccess: close });
        }}
      >
        <label className={ui.field}>
          <span className={ui.label}>{t('members.pitchDialog.label')}</span>
          <input
            className={`${ui.input} ${styles.pitch}`}
            type="number"
            inputMode="decimal"
            min={400}
            max={480}
            step={0.5}
            value={value}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <p className={styles.text}>{t('members.pitchDialog.hint')}</p>
        {change.isError && (
          <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
            {errorText(change.error, t)}
          </div>
        )}
        <div className={styles.buttons}>
          <button type="button" className={ui.button} onClick={close}>
            {t('common.cancel')}
          </button>
          <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={!valid || change.isPending}>
            {t('members.pitchDialog.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
